import React, { useState } from "react";
import { ChevronDown, History, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SegmentedReadingPreview } from "@/components/SegmentedReadingPreview";
import { TechnicalHistory } from "@/components/TechnicalHistory";
import { useResultReview } from "./primitives";

/** Indice evidenze reali per segmento/pagina (testo persistito, clic = pagina PDF). */
export const EvidenceIndex = ({ d }) => {
  const { select, selected } = useResultReview();
  const entries = Object.entries(d.evidence_by_segment);
  const total = entries.reduce((n, [, l]) => n + l.length, 0);
  return (
    <details className="border border-border bg-white" data-testid="result-evidence-index">
      <summary className="cursor-pointer select-none px-2 py-1.5 bg-muted text-[11px] font-semibold flex justify-between items-center">
        <span>Indice evidenze ({total}) — testo letto dal disegno, per segmento e pagina</span><ChevronDown size={14} />
      </summary>
      <div className="p-2 grid grid-cols-1 lg:grid-cols-2 gap-1 max-h-72 overflow-auto">
        {entries.flatMap(([segId, list]) => list.map((e) => {
          const sel = selected && selected.segId === segId && selected.evId === e.evidence_id;
          return (
            <button key={`${segId}-${e.evidence_id}`} type="button" onClick={() => select(segId, e.evidence_id)}
              className={`text-left px-1.5 py-1 border text-[10px] ${sel ? "border-red-500 bg-red-50" : "border-border hover:bg-muted/60"}`}
              data-testid={`result-evidence-${segId}-${e.evidence_id}`}>
              <span className="font-mono font-bold">{segId}/{e.evidence_id}</span> <span className="text-muted-foreground">p.{e.page}</span> — {e.text}
            </button>
          );
        }))}
      </div>
    </details>
  );
};

/** Sezioni secondarie: dry-plan (dialog esistente, read-only) e cronologia tecnica. Mai vista predefinita. */
export const SecondarySections = ({ d }) => {
  const [openPlan, setOpenPlan] = useState(false);
  const job = d.job;
  return (
    <details className="border border-border bg-white" data-testid="result-secondary-sections">
      <summary className="cursor-pointer select-none px-2 py-1.5 bg-muted text-[11px] font-semibold flex justify-between items-center">
        <span className="flex items-center gap-1"><History size={12} />Dry-plan, cronologia tecnica e stato legacy (sezione secondaria)</span><ChevronDown size={14} />
      </summary>
      <div className="p-2 space-y-2 text-[11px]">
        <div className="border border-border p-1.5" data-testid="result-legacy-status">
          Stato riga coda: <b>{job.legacy_status}</b> ({job.legacy_reader_version}, aggiornato {String(job.legacy_status_updated_at).slice(0, 16)}Z, costo ${job.legacy_real_cost_usd}).
          <span className="text-muted-foreground"> {job.legacy_status_note}. Il risultato mostrato in questa pagina proviene dal run <span className="font-mono">{d.run.run_id}</span> ({d.run.status}).</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpenPlan(true)} data-testid="result-open-dryplan-btn">
            <Layers size={12} className="mr-1" />Apri dry-plan e pannelli run (read-only)
          </Button>
          <span className="text-[10px] text-muted-foreground">Dry-plan deterministico e pannelli operativi: nessun avvio, nessuna chiamata provider.</span>
        </div>
        <TechnicalHistory jobId={job.id} />
        {openPlan && <SegmentedReadingPreview jobId={job.id} open={openPlan} onClose={() => setOpenPlan(false)} />}
      </div>
    </details>
  );
};
